import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Collapse, Link } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DownloadIcon from "@iconify/icons-mdi/download";
import PDFIcon from "@iconify/icons-mdi/file-pdf-box-outline";
import ShowIcon from "@iconify/icons-mdi/show";
import { InlineIcon } from "@iconify/react";
import {
  MyHrAttachmentDialog,
  MyHrDataRequestSubCollapseHeader,
} from "my-hr/components";
import { useMyHrChangeRequestAttachement } from "my-hr/hooks";

interface MyHrDataRequestAttachmentProps {
  approvedAt: Date | string | null;
  rejectedAt: Date | string | null;
  typeGroup?: string;
  type: string;
  employeeCode: string;
  isMobile: boolean;
}

export const MyHrDataRequesAttachment = ({
  approvedAt,
  rejectedAt,
  typeGroup,
  type,
  employeeCode,
  isMobile,
}: MyHrDataRequestAttachmentProps) => {
  const theme = useTheme();
  const { t } = useTranslation();

  let attachementType = typeGroup ? typeGroup : type;
  if (typeGroup === "phones" || typeGroup === "emails") {
    attachementType = type;
  }

  const { data } = useMyHrChangeRequestAttachement(
    attachementType,
    employeeCode,
  );

  const [attachmentExpanded, setAttachmentExpanded] = useState(false);
  const [fullScreenVisible, setFullScreenVisible] = useState(false);
  const [selectedFile, setSelectedFile] = useState<{
    slug?: string;
    filename?: string;
  } | null>(null);

  const handleAttachmentExpandClick = () => {
    setAttachmentExpanded(!attachmentExpanded);
  };

  const removeAssetTag = (filename: string) => {
    let filenameArray = filename.split("/");
    return filenameArray[filenameArray.length - 1];
  };

  const handleCancel = () => {
    setFullScreenVisible(false);
  };

  return (
    <>
      <MyHrDataRequestSubCollapseHeader
        expanded={attachmentExpanded}
        handleExpandClick={handleAttachmentExpandClick}
        label={t("MY_HR_ATTACHMENTS")}
        badge={data.length + " " + t("MY_HR_ATTACHMENT")}
        data={data}
        isMobile={isMobile}
        type="attachments"
      />
      <Collapse
        in={
          approvedAt === null && rejectedAt === null
            ? attachmentExpanded
            : false
        }
        timeout="auto"
      >
        <Box
          maxWidth="600px"
          style={{
            backgroundColor: theme.palette.white.main,
            padding: "0px 20px 0px 20px",
            borderBottomLeftRadius: "4px",
            borderBottomRightRadius: "4px",
          }}
        >
          <Box
            maxWidth={isMobile ? "auto" : "600px"}
            style={{
              height: "1px",
              backgroundColor: theme.palette.gray40.main,
              width: isMobile ? "auto" : "600px",
            }}
            marginBottom="20px"
          />
          {data.map((item: any, index: number) => {
            return (
              <Box key={`${item}-${index}`}>
                <Box
                  display="flex"
                  flexDirection="row"
                  alignItems="center"
                  height={isMobile ? "auto" : "72px"}
                  border={"1px solid #C6CACC"}
                  style={{
                    borderRadius: "8px",
                    paddingLeft: "20px",
                    backgroundColor: theme.palette.gray20.main,
                  }}
                >
                  <InlineIcon
                    icon={PDFIcon}
                    width="24px"
                    style={{ color: "#4d4d4d" }}
                  />
                  <Box width="14px" />
                  <Box display="flex" flexDirection="column" flexGrow={1}>
                    <Box>
                      <Box key={`${index}_${item._id}`}>
                        {removeAssetTag(item.filename)}
                        <Box
                          style={{
                            color: "#919699",
                            fontSize: "12px",
                            marginTop: "3px",
                          }}
                        >
                          {new Date(item.createdAt).toLocaleDateString()}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                  <Box>
                    <InlineIcon
                      id="showIcon"
                      icon={ShowIcon}
                      width="24px"
                      style={{
                        color: "#4d4d4d",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        const isPdf = item.filename
                          .toLowerCase()
                          .endsWith(".pdf");
                        if (isPdf) {
                          window.open(item?.filename, "_blank");
                        } else {
                          setSelectedFile({
                            ...item,
                          });
                          setFullScreenVisible(true);
                        }
                      }}
                    />

                    <MyHrAttachmentDialog
                      showDialog={fullScreenVisible}
                      handleCancel={handleCancel}
                      fileData={selectedFile ?? {}}
                    />
                  </Box>
                  <Box width="10px" />
                  <Box paddingRight="16px">
                    <Link href={item.filename} target={item.filename} download>
                      <InlineIcon
                        id="downloadIcon"
                        icon={DownloadIcon}
                        width="24px"
                        style={{
                          color: "#4d4d4d",
                          cursor: "pointer",
                        }}
                      />
                    </Link>
                  </Box>
                </Box>
                <Box height="16px" />
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </>
  );
};

export default MyHrDataRequesAttachment;
